import { useMemo, useRef } from 'react';
import useInfiniteStaticProducts from 'hooks/useInfiniteStaticProducts';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import getQualifyingHitVariants from 'components/consumer/category-page-algolia/getQualifyingHitVariants';
import normalizeOptionSku from 'components/consumer/category-page-algolia/normalizeOptionSku';
import { useDeferredValue } from '../../../hooks/reactShims';
import { getVariantsByMatchedColorFirst, buildProductsInAlgoliaOrder } from '../utils';

/**
 * Derives product IDs from Algolia hits, fetches full product data, maps
 * variant highlighting, orders results to match Algolia ranking, and keeps
 * previously-visible cards only while they still belong to the current hit
 * set and were filtered for the current `variantMatching` (e.g. GraphQL
 * enrichment in flight). After a filter/query change the hit IDs differ, and
 * after a warehouse change the allowlist does — stale cards are dropped so
 * the grid shows a skeleton instead of the previous unfiltered (or previous
 * warehouse's) products. If enrichment returns nothing for the current hits,
 * the grid stays empty (no stale fallback).
 *
 * `variantMatching` ({ variantWarehouseField, warehouseId,
 * keepUnmatchedProducts }) restricts each product card's upholstery variants
 * to the SKUs that qualify at the user's warehouse (from the hit's
 * `product_variants`); a product with no match is dropped — or, with
 * `keepUnmatchedProducts` (CON-8567 zero-drop fallback), kept with its full
 * variant list. Cards stay one-per-product (CON-8561 decision (b)).
 */
const useDisplayProducts = ({
  items,
  searchBoxQuery,
  results,
  seed,
  variantMatching = null
}) => {
  const previousProductsRef = useRef([]);
  // The variant-matching identity the cards in `previousProductsRef` were
  // filtered for — see `displayProducts`.
  const previousMatchingKeyRef = useRef(null);
  const {
    selectedColors,
    currentProductIndex
  } = useAdvancedSearchContext();

  // Derived rather than compared by reference, so an inline `variantMatching`
  // object does not defeat the previous-cards fallback on every render.
  const variantMatchingKey = variantMatching ? [variantMatching.variantWarehouseField, variantMatching.warehouseId, variantMatching.keepUnmatchedProducts === true].join('|') : null;
  const selectedColorLabels = useMemo(() => Object.entries(selectedColors || {}).filter(([, isSelected]) => isSelected).map(([color]) => color), [selectedColors]);

  // Prefer SearchBox query – results.query can be stale/empty when filters
  // change (e.g. ready to ship)
  const searchKey = useMemo(() => {
    const query = searchBoxQuery || results?.query || '';
    const index = currentProductIndex || results?.index || '';
    const params = results?.params || '';
    const paramsWithoutPage = params.split('&').filter(param => !param.startsWith('page=')).join('&');
    return `${index}-${query}-${paramsWithoutPage}`;
  }, [currentProductIndex, searchBoxQuery, results?.query, results?.index, results?.params]);
  const productIdsRaw = useMemo(() => {
    if (!items?.length) return [];
    return items.map(hit => {
      const id = hit.product_id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id_0 => id_0 > 0 && !Number.isNaN(id_0));
  }, [items]);
  const currentHitIds = useMemo(() => new Set(productIdsRaw.map(id_1 => String(id_1))), [productIdsRaw]);

  // Defer product IDs so we don't trigger immediate re-fetches while typing
  const productIds = useDeferredValue(productIdsRaw);

  // The hit's qualifying SKUs keyed by product id, normalized to the bare
  // option SKU — the index and the enriched variants disagree on SKU format,
  // so the join key is normalized on both sides. Products absent from the map
  // (no SKU qualifies at this warehouse) drop before enrichment output.
  const matchedSkusByProductId = useMemo(() => {
    if (!variantMatching) return null;
    const map = new Map();
    (items ?? []).forEach(hit_0 => {
      const qualifyingVariants = getQualifyingHitVariants({
        hit: hit_0,
        ...variantMatching
      });
      if (qualifyingVariants.length) {
        map.set(String(hit_0.product_id), new Set(qualifyingVariants.map(variant => normalizeOptionSku(variant?.sku))));
      }
    });
    return map;
  }, [items, variantMatching]);
  const {
    products: productsWithVariantsToShow,
    loading
  } = useInfiniteStaticProducts({
    productIds,
    searchKey,
    seed,
    matchedSkusByProductId,
    keepUnmatchedProducts: variantMatching?.keepUnmatchedProducts === true
  }, {
    skipSorting: true
  });
  const productsWithHighlightedVariantsFirst = useMemo(() => productsWithVariantsToShow.map(product => ({
    ...product,
    variantsToShow: getVariantsByMatchedColorFirst(product, items, selectedColorLabels)
  })), [productsWithVariantsToShow, items, selectedColorLabels]);

  // `results.index` is the index that produced these hits and their `__queryID`;
  // during a sort change `currentProductIndex` advances a render earlier, so
  // preferring it would pair a queryID with the wrong index and break Insights.
  const productsInAlgoliaOrder = useMemo(() => buildProductsInAlgoliaOrder(productsWithHighlightedVariantsFirst, items, results?.index || currentProductIndex, variantMatching), [items, productsWithHighlightedVariantsFirst, currentProductIndex, results?.index, variantMatching]);

  // Keep previous products visible until enrichment is ready only when they
  // still match the current Algolia hits (pagination / same-result enrichment).
  // When Algolia returns 0 hits, or hits change to a different ID set (filter
  // refine), clear so the parent can show a skeleton / empty instead of stale
  // cards. When enrichment finishes empty for these hits, stay empty.
  const displayProducts = useMemo(() => {
    if (!items?.length) {
      previousProductsRef.current = [];
      return [];
    }
    if (productsInAlgoliaOrder?.length > 0) {
      previousProductsRef.current = productsInAlgoliaOrder;
      previousMatchingKeyRef.current = variantMatchingKey;
      return productsInAlgoliaOrder;
    }

    // Cards filtered for another warehouse (or SKU allowlist) are never a
    // valid stand-in, even when the hit ids line up: they would show the
    // previous warehouse's swatches and quickship badges until enrichment
    // lands.
    const previous = previousMatchingKeyRef.current === variantMatchingKey ? previousProductsRef.current : [];
    const previousIds = new Set((previous || []).map(product_0 => String(product_0?.id ?? '')));
    // Subset, not equality: Load more (and widening a filter) grows
    // currentHitIds while previous remains a valid subset. Requiring equal
    // size would flash a skeleton on pagination. Clear only when a previous
    // id is gone (narrowed / different filter).
    const previousStillMatchesCurrentHits = previousIds.size > 0 && [...previousIds].every(id_2 => currentHitIds.has(id_2));
    if (previousStillMatchesCurrentHits) {
      return previous;
    }
    previousProductsRef.current = [];
    return [];
  }, [items, currentHitIds, productsInAlgoliaOrder, variantMatchingKey]);

  // How many cards the loaded hits can produce once enrichment lands: hits
  // with at least one qualifying SKU. An upper bound — a qualifying SKU with
  // no enriched (upholstery-only) counterpart still drops its product later —
  // but exact for the zero case, which is what the caller needs to tell "cards
  // are coming" from "this page can never render one". On a zero-drop page
  // type every enriched hit produces a card, so the hit count is the bound
  // (enrichment can still return fewer products than hits).
  const expectedCardCount = matchedSkusByProductId && !variantMatching?.keepUnmatchedProducts ? matchedSkusByProductId.size : items?.length ?? 0;
  return {
    displayProducts,
    loading,
    productIds,
    productIdsRaw,
    expectedCardCount
  };
};
export default useDisplayProducts;